import { memo, useCallback } from 'react';
import type { FC } from 'react';

import classNames from 'classnames';
import type { ProfileSliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';

import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchProfiles } from '../../../../modules/profiles';
import { Profile } from '../../../Profile/Profile';
import { usePlaceholders } from '../../usePlaceholders';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { getImageSources } from '../../../../helpers/images';
import { trackClickEvent } from '../../../../services/tracking/pulseEvents';
import {
  getPath,
  pathToUrl,
  routeNames,
} from '../../../../shared/helpers/routes';
import { useComponentProfiles } from '../../../utils/useComponentProfiles';

import * as styles from './ProfilesSlider.module.scss';

type Props = {
  componentConfig: ProfileSliderComponent;
  pageComponentIndex: number;
};

const ProfilesSliderBase: FC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchProfiles,
  });

  const { id, header } = componentConfig;

  const profiles = useComponentProfiles(id);

  const placeholders = usePlaceholders(isFetched);

  const handleProfileClick = useCallback(
    (id: CnpTagId, title: string, columnIndex: number) => {
      const parent = {
        elementType: 'ProfilesSlider',
        name: componentConfig.header,
        row: pageComponentIndex,
      };

      const profile = {
        id,
        title,
        elementType: 'Profile',
        position: columnIndex,
      };

      const url = pathToUrl(getPath(routeNames.tag, { tagId: id, title }));

      const target = {
        url,
        title,
        type: 'Tag',
      };

      trackClickEvent(parent, profile, target);
    },
    [componentConfig.header, pageComponentIndex]
  );

  if (isFetched && profiles.length === 0) {
    return null;
  }

  return (
    <Slider header={header} className={styles.slider}>
      {[
        ...placeholders.map((item, index) => (
          <Placeholder type={placeholderTypes.profile} key={index} />
        )),
        ...profiles.map((profile, index) => {
          const { id, title, images } = profile;
          const imageUrl = images ? images.main : undefined;

          const imageSources = imageUrl
            ? getImageSources({
                imageUrl,
                imagePreset: 'profilesSlider',
                aspectRatio: 'square',
              })
            : undefined;

          return (
            <Profile
              key={id}
              id={id}
              name={title}
              imageSources={imageSources!}
              className={styles.profile}
              onClick={() => handleProfileClick(id, title, index)}
            />
          );
        }),
      ]}
    </Slider>
  );
};

export const ProfilesSlider = memo(ProfilesSliderBase);
